/**
 * IDEA CANVAS - Embedded Image Generation Form
 * Handles the IMAGE tab functionality
 *
 * Extracted from idea-canvas.js for better modularity
 * @version 1.0
 */

import { GeneratorState, API_CONFIG } from '../../../features/generator/core/state.js';
import { STYLE_GUIDES, getEffectiveStyle } from '../../../features/generator/features/image-generation.js';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Check if a value is a valid string (not null, undefined, empty, or literal "null")
 * Used to prevent "null" strings from appearing in image prompts
 * @param {any} value - The value to check
 * @returns {boolean} - True if value is a valid non-empty string
 */
function isValidString(value) {
  return value &&
         typeof value === 'string' &&
         value.trim() !== '' &&
         value.toLowerCase() !== 'null';
}

// ============================================================================
// STATE
// ============================================================================

let providersCache = null;

let canvasImageGenState = {
  isGenerating: false,
  requestId: null,
  modelId: null
};

// Image generation API endpoints
const IMAGE_GEN_ENDPOINTS = {
  providers: '/api/image-gen/providers',
  submit: '/api/image-gen/submit',
  status: (modelId, requestId) => `/api/image-gen/status/${encodeURIComponent(modelId)}/${requestId}`,
  result: (modelId, requestId) => `/api/image-gen/result/${encodeURIComponent(modelId)}/${requestId}`
};

// ============================================================================
// PROVIDERS
// ============================================================================

/**
 * Load image generation providers from backend
 * @returns {Promise<Array>} Array of provider objects
 */
export async function loadProviders() {
  if (providersCache) return providersCache;

  try {
    const response = await fetch(API_CONFIG.baseUrl + IMAGE_GEN_ENDPOINTS.providers);
    const data = await response.json();
    if (data.success && data.providers) {
      providersCache = data.providers;
      return providersCache;
    }
  } catch (error) {
    console.error('[ImageForm] Failed to load providers:', error);
  }

  // Fallback defaults
  providersCache = [
    { id: 'seedream', name: 'ByteDance SeedReam v4.5', shortName: 'SeedReam', costPerImage: 0.04 },
    { id: 'nano-banana', name: 'Nano Banana Pro', shortName: 'Nano Banana', costPerImage: 0.15 }
  ];
  return providersCache;
}

// ============================================================================
// PROMPT BUILDING
// ============================================================================

/**
 * Build image prompt from idea data
 * @param {Object} idea - The idea object
 * @param {string} aspectRatio - Aspect ratio (e.g., '4:3')
 * @returns {string} The generated prompt
 */
export function buildImagePrompt(idea, aspectRatio = '4:3') {
  const brief = GeneratorState?.brief || {};
  const format = (idea.format || 'print').toLowerCase();

  // Video format: storyboard grid prompt
  if (format === 'video') {
    const product = brief.product || 'Product';
    const title = idea.title || 'Untitled';
    const visualDescription = idea.text || idea.visual || '';
    const headline = idea.headline && idea.headline !== '[Visual Only]' ? idea.headline : '';

    return `Create a professional 6-panel film storyboard grid.
Layout: 3 rows x 2 columns.
Each panel: Photorealistic cinematic film still, 16:9 panel aspect ratio.
No text, labels, annotations, or frame numbers on panels.

STYLE: Hollywood production quality, anamorphic lens, cinematic color grading, dramatic lighting.

Product: ${product}
${title}

${visualDescription}

${headline ? `SUPER: "${headline}"` : ''}

TECHNICAL REQUIREMENTS:
- Consistent lighting and color palette across all 6 panels
- ARRI Alexa / RED camera aesthetic
- Anamorphic lens bokeh
- Professional color grading
- Clean frames, no storyboard overlay

--ar 3:2 --v 6.1 --style raw`;
  }

  // Other formats: standard image prompt
  const formatName = format === 'print' ? 'print ad' :
                     format === 'social' ? 'social media post' :
                     format === 'experiential' ? 'brand activation' :
                     format === 'digital' ? 'digital banner' :
                     format === 'campaign' ? 'campaign visual' : 'ad';

  let prompt = `Create this ${formatName} (${aspectRatio}):\n\n`;
  prompt += `Product: ${brief.product || 'Product'}\n\n`;

  if (idea.text || idea.visual) {
    prompt += idea.text || idea.visual;
  }

  if (idea.headline && idea.headline !== '[Visual Only]') {
    prompt += `\n\nHeadline: "${idea.headline}"`;
    if (isValidString(idea.subline)) {
      prompt += `\nSubline: "${idea.subline}"`;
    }
  }

  return prompt;
}

/**
 * Build styled prompt with style guide prepended
 * @param {Object} idea - The idea object
 * @param {string} aspectRatio - Aspect ratio
 * @param {string} style - Style name
 * @returns {string} The styled prompt
 */
export function buildStyledPrompt(idea, aspectRatio = '4:3', style = 'auto') {
  const basePrompt = buildImagePrompt(idea, aspectRatio);
  const effectiveStyle = getEffectiveStyle(style);
  const styleGuide = STYLE_GUIDES[effectiveStyle] || '';

  if (!styleGuide) {
    return basePrompt;
  }

  return `${styleGuide}\n\n${basePrompt}`;
}

// ============================================================================
// MODEL & ASPECT HANDLERS
// ============================================================================

/**
 * Handle model selector button click
 * @param {HTMLElement} btn - The clicked button
 * @param {Function} refreshPromptFn - Function to refresh the prompt
 */
export function handleModelSelect(btn, refreshPromptFn) {
  const container = document.getElementById('canvas-model-selector');
  if (!container) return;

  container.querySelectorAll('.canvas-model-btn').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');

  // Update cost display
  const costDisplay = document.querySelector('#canvas-image-cost .cost-value');
  if (costDisplay) {
    const cost = btn.dataset.cost;
    costDisplay.textContent = `$${parseFloat(cost).toFixed(2)}`;
  }

  // Update prompt with current aspect ratio
  if (refreshPromptFn) refreshPromptFn();
}

/**
 * Handle aspect ratio button click
 * @param {HTMLElement} btn - The clicked button
 * @param {Function} refreshPromptFn - Function to refresh the prompt
 */
export function handleAspectSelect(btn, refreshPromptFn) {
  const container = document.getElementById('canvas-aspect-selector');
  if (!container) return;

  container.querySelectorAll('.canvas-aspect-btn').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');

  // Refresh prompt with new aspect ratio
  if (refreshPromptFn) refreshPromptFn();
}

/**
 * Refresh canvas image prompt with current settings
 * @param {Object} currentIdea - The current idea
 */
export function refreshCanvasImagePrompt(currentIdea) {
  if (!currentIdea) return;

  const aspectRatio = document.querySelector('.canvas-aspect-btn.active')?.dataset.ratio || '4:3';
  const style = document.getElementById('canvas-image-style')?.value || 'auto';
  const promptTextarea = document.getElementById('canvas-image-prompt');

  if (promptTextarea) {
    promptTextarea.value = buildStyledPrompt(currentIdea, aspectRatio, style);
  }
}

// ============================================================================
// IMAGE TAB POPULATION
// ============================================================================

/**
 * Populate IMAGE tab with form data
 * @param {Object} idea - The idea object
 */
export async function populateImageTab(idea) {
  // Load and render model selector
  const providers = await loadProviders();
  const modelContainer = document.getElementById('canvas-model-selector');

  if (modelContainer && providers) {
    // For video format, default to nano-banana (index 1), otherwise seedream (index 0)
    const defaultIndex = idea.format?.toLowerCase() === 'video' ? 1 : 0;

    modelContainer.innerHTML = providers.map((provider, index) => `
      <button class="canvas-model-btn ${index === defaultIndex ? 'active' : ''}"
              data-provider="${provider.id}"
              data-cost="${provider.costPerImage}">
        <span class="model-name">${provider.shortName}</span>
        <span class="model-cost">$${provider.costPerImage.toFixed(2)}</span>
      </button>
    `).join('');

    // Update cost display
    const activeModel = modelContainer.querySelector('.canvas-model-btn.active');
    if (activeModel) {
      const costDisplay = document.querySelector('#canvas-image-cost .cost-value');
      if (costDisplay) {
        costDisplay.textContent = `$${parseFloat(activeModel.dataset.cost).toFixed(2)}`;
      }
    }
  }

  // Build and set initial prompt
  const aspectRatio = document.querySelector('.canvas-aspect-btn.active')?.dataset.ratio || '4:3';
  const style = document.getElementById('canvas-image-style')?.value || 'auto';
  const promptTextarea = document.getElementById('canvas-image-prompt');

  if (promptTextarea) {
    promptTextarea.value = buildStyledPrompt(idea, aspectRatio, style);
  }

  // Add change listener for style dropdown (once)
  const styleSelect = document.getElementById('canvas-image-style');
  if (styleSelect && !styleSelect._canvasListenerAdded) {
    styleSelect.addEventListener('change', () => refreshCanvasImagePrompt(idea));
    styleSelect._canvasListenerAdded = true;
  }
}

// ============================================================================
// GENERATING PLACEHOLDER
// ============================================================================

/**
 * Show generating placeholder in canvas left column
 * @param {HTMLElement} canvasInner - The canvas inner element
 * @param {string} type - 'image' or 'video'
 */
export function showCanvasGeneratingPlaceholder(canvasInner, type = 'image') {
  const leftColumn = canvasInner?.querySelector('.canvas-col-image');
  if (!leftColumn) return;

  const text = type === 'video' ? 'RENDERING VIDEO...' : 'GENERATING IMAGE...';
  const subtext = type === 'video' ? 'This may take 1-2 minutes' : 'This may take a few seconds';

  leftColumn.innerHTML = `
    <div class="canvas-placeholder generating">
      <div class="pixel-loader"></div>
      <div class="canvas-placeholder-text">${text}</div>
      <div class="canvas-placeholder-subtext">${subtext}</div>
    </div>
  `;
}

// ============================================================================
// IMAGE GENERATION
// ============================================================================

/**
 * Handle embedded image generation
 * @param {Object} currentIdea - The current idea
 * @param {HTMLElement} canvasInner - The canvas inner element
 * @param {Object} callbacks - Callback functions
 */
export async function handleEmbeddedImageGenerate(currentIdea, canvasInner, callbacks = {}) {
  if (!currentIdea || canvasImageGenState.isGenerating) return;

  const provider = document.querySelector('.canvas-model-btn.active')?.dataset.provider || 'seedream';
  const aspectRatio = document.querySelector('.canvas-aspect-btn.active')?.dataset.ratio || '4:3';
  const style = document.getElementById('canvas-image-style')?.value || 'auto';
  const prompt = buildStyledPrompt(currentIdea, aspectRatio, style);

  // Update button state
  const submitBtn = document.getElementById('canvas-btn-generate-image');
  const btnText = submitBtn?.querySelector('.btn-text');
  const btnLoading = submitBtn?.querySelector('.btn-loading');

  if (btnText) btnText.style.display = 'none';
  if (btnLoading) btnLoading.style.display = 'inline';
  if (submitBtn) submitBtn.disabled = true;

  canvasImageGenState.isGenerating = true;

  try {
    // FIX IMG-5: Import and get auth token for canvas image generation
    const { getSessionToken } = await import('../../../features/auth/js/client.js');
    const token = await getSessionToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    // Submit to queue - FIX IMG-5: Include auth header
    const response = await fetch(API_CONFIG.baseUrl + IMAGE_GEN_ENDPOINTS.submit, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prompt, provider, aspectRatio })
    });

    const data = await response.json();

    if (data.success) {
      canvasImageGenState.requestId = data.requestId;
      canvasImageGenState.modelId = data.modelId;

      // Show generating placeholder in left column
      showCanvasGeneratingPlaceholder(canvasInner, 'image');

      if (window.Toast) window.Toast.info('Image generation started...');

      // Start polling
      startCanvasImagePolling(String(currentIdea.id), data.modelId, data.requestId, {
        provider,
        aspectRatio
      }, callbacks);
    } else {
      throw new Error(data.error || 'Generation failed');
    }
  } catch (error) {
    console.error('[ImageForm] Image generation error:', error);
    if (window.Toast) window.Toast.error('Failed to generate image: ' + error.message);

    // Reset button state on error
    resetCanvasImageGenState();
  }
}

/**
 * Poll for canvas image generation result
 * @param {string} ideaId - The idea ID
 * @param {string} modelId - The model ID
 * @param {string} requestId - The request ID
 * @param {Object} meta - Metadata (provider, aspectRatio)
 * @param {Object} callbacks - Callback functions
 */
export function startCanvasImagePolling(ideaId, modelId, requestId, meta, callbacks = {}) {
  // FIX IMG-3: Add timeout tracking for polling
  const MAX_POLL_ATTEMPTS = 150; // 5 minutes at 2-second intervals
  let pollAttempts = 0;

  const pollInterval = setInterval(async () => {
    // FIX IMG-3: Check for timeout
    pollAttempts++;
    if (pollAttempts > MAX_POLL_ATTEMPTS) {
      clearInterval(pollInterval);
      console.error('[ImageForm] Polling timed out after', MAX_POLL_ATTEMPTS, 'attempts');
      if (window.Toast) window.Toast.error('Image generation timed out. Please try again.');
      resetCanvasImageGenState();
      return;
    }

    try {
      // Get auth headers for polling requests
      const { getSessionToken } = await import('../../../features/auth/js/client.js');
      const token = await getSessionToken();
      const pollHeaders = {};
      if (token) pollHeaders['Authorization'] = `Bearer ${token}`;

      const statusUrl = API_CONFIG.baseUrl + IMAGE_GEN_ENDPOINTS.status(modelId, requestId);
      const response = await fetch(statusUrl, { headers: pollHeaders });

      // FIX IMG-1: Check HTTP status before parsing JSON
      if (!response.ok) {
        console.error('[ImageForm] Polling HTTP error:', response.status, response.statusText);
        // Don't crash the loop - wait for next poll or eventual timeout
        return;
      }

      const data = await response.json();

      if (data.status === 'COMPLETED') {
        clearInterval(pollInterval);

        // Get result with auth
        const resultUrl = API_CONFIG.baseUrl + IMAGE_GEN_ENDPOINTS.result(modelId, requestId);
        const resultResponse = await fetch(resultUrl, { headers: pollHeaders });
        const resultData = await resultResponse.json();

        // Extract image URL from response
        let imageUrl = null;
        if (resultData.data?.data?.images?.length > 0) {
          imageUrl = resultData.data.data.images[0].url;
        } else if (resultData.data?.images?.length > 0) {
          imageUrl = resultData.data.images[0].url;
        } else if (resultData.images?.length > 0) {
          imageUrl = resultData.images[0].url;
        }

        if (imageUrl) {
          console.log('[ImageForm] Image generation complete:', { ideaId, imageUrl: imageUrl.substring(0, 50) });

          // STEP 1: Upload to R2 FIRST to get permanent URL
          const module = await import('../../../features/generator/features/image-generation.js');
          let permanentUrl = imageUrl;

          // Upload to R2 for permanent storage (fal.ai URLs expire!)
          if (imageUrl.includes('fal.media') || imageUrl.includes('v3b.fal.media')) {
            try {
              const { getSessionToken } = await import('../../../features/auth/js/client.js');
              const token = await getSessionToken();
              const headers = { 'Content-Type': 'application/json' };
              if (token) headers['Authorization'] = `Bearer ${token}`;

              const r2Response = await fetch(`${API_CONFIG.baseUrl}/api/storage/upload-url`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ sourceUrl: imageUrl })
              });
              const r2Result = await r2Response.json();
              if (r2Result.success) {
                permanentUrl = r2Result.url;
                console.log('[ImageForm] R2 upload success:', permanentUrl.substring(0, 60));
              }
            } catch (r2Error) {
              console.warn('[ImageForm] R2 upload failed, using temp URL:', r2Error);
            }
          }

          // Update preloaded images with permanent URL
          if (!GeneratorState.preloadedImages) {
            GeneratorState.preloadedImages = {};
          }
          GeneratorState.preloadedImages[ideaId] = permanentUrl;

          // Call the onComplete callback with permanent URL
          if (callbacks.onComplete) {
            callbacks.onComplete(ideaId, permanentUrl, meta);
          }

          if (window.Toast) window.Toast.success('Image generated!');

          // STEP 2: Save permanent URL to database
          const result = await module.saveImageToDatabase(ideaId, permanentUrl, meta);
          if (!result.success) {
            console.error('[ImageForm] Failed to save image:', result.error);
            // Retry once after delay
            setTimeout(async () => {
              const retryResult = await module.saveImageToDatabase(ideaId, permanentUrl, meta);
              if (!retryResult.success) {
                console.error('[ImageForm] Retry also failed');
              }
            }, 2000);
          }

          // Refresh the canvas to show the new image
          if (window.IdeaCanvas?.isOpen?.()) {
            const currentIdea = window.IdeaCanvas.getCurrentIdea?.();
            if (currentIdea && String(currentIdea.id) === ideaId) {
              currentIdea.imageUrl = permanentUrl;
              currentIdea.image_url = permanentUrl;
              window.IdeaCanvas.refresh?.();
              console.log('[ImageForm] Refreshed canvas with new image');
            }
          }
        } else {
          if (window.Toast) window.Toast.error('No image returned');
        }

        // Reset state
        resetCanvasImageGenState();

      } else if (data.status === 'FAILED') {
        clearInterval(pollInterval);
        if (window.Toast) window.Toast.error('Image generation failed');
        resetCanvasImageGenState();
      }
      // else still processing, continue polling

    } catch (error) {
      console.error('[ImageForm] Polling error:', error);
    }
  }, 2000);
}

/**
 * Reset canvas image generation state and UI
 */
export function resetCanvasImageGenState() {
  const submitBtn = document.getElementById('canvas-btn-generate-image');
  const btnText = submitBtn?.querySelector('.btn-text');
  const btnLoading = submitBtn?.querySelector('.btn-loading');

  if (btnText) btnText.style.display = 'inline';
  if (btnLoading) btnLoading.style.display = 'none';
  if (submitBtn) submitBtn.disabled = false;

  canvasImageGenState.isGenerating = false;
  canvasImageGenState.requestId = null;
  canvasImageGenState.modelId = null;
}

/**
 * Get the current image generation state
 * @returns {Object} The image generation state
 */
export function getCanvasImageGenState() {
  return { ...canvasImageGenState };
}
